// Custom ReactFlow node for action blocks (green)
import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { Box, Typography, Chip } from '@mui/material';
import { FlashOn as ActionIcon } from '@mui/icons-material';

// Smart label for debug output based on block type
const getDebugLabel = (debugStep) => {
  if (!debugStep?.output) return null;
  const out = debugStep.output;
  const bt = debugStep.blockType;

  if (bt === 'http_request') {
    return `${out.statusCode || '?'} ${out.statusMessage || ''}`.trim();
  }
  if (bt === 'extract_params' || bt === 'regex_extract') {
    const extracted = out.extracted || out;
    const count = typeof extracted === 'object' ? Object.keys(extracted).length : 0;
    return `${count} var${count !== 1 ? 's' : ''}`;
  }
  if (bt === 'delay') {
    return out.delayMs ? `${out.delayMs}ms` : null;
  }
  if (bt === 'json_builder') {
    return out.outputVariable ? `→ {{${out.outputVariable}}}` : null;
  }
  if (bt === 'set_output') {
    return out.value !== undefined ? `= ${out.value}` : null;
  }
  if (typeof out === 'string') return out.substring(0, 30);
  if (typeof out === 'object') {
    const str = JSON.stringify(out);
    return str.length > 30 ? str.substring(0, 30) + '...' : str;
  }
  return null;
};

const ActionNode = memo(({ data, selected }) => {
  const debugStep = data._debugStep;
  const debugLabel = debugStep ? getDebugLabel(debugStep) : null;

  return (
    <Box sx={{
      minWidth: 180,
      border: selected ? '2px solid #2e7d32' : '1px solid #388e3c',
      borderRadius: 1,
      bgcolor: 'background.paper',
      boxShadow: selected ? 3 : 1,
      ...(debugStep && {
        boxShadow: debugStep.status === 'completed'
          ? '0 0 8px rgba(46,125,50,0.4)' : debugStep.status === 'failed'
            ? '0 0 8px rgba(198,40,40,0.4)' : 1,
      }),
    }}>
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: '#388e3c', width: 10, height: 10 }}
      />

      {/* Header */}
      <Box sx={{
        px: 1.5, py: 0.75,
        bgcolor: '#388e3c',
        color: 'white',
        borderRadius: '3px 3px 0 0',
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
      }}>
        <ActionIcon sx={{ fontSize: 16 }} />
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem' }}>
          Action
        </Typography>
        {debugStep && (
          <Chip
            label={debugStep.status === 'completed' ? 'OK' : 'ERR'}
            size="small"
            sx={{
              ml: 'auto',
              height: 16,
              fontSize: '0.55rem',
              fontWeight: 700,
              bgcolor: debugStep.status === 'completed' ? 'rgba(255,255,255,0.25)' : 'rgba(198,40,40,0.7)',
              color: 'white',
            }}
          />
        )}
      </Box>

      {/* Body */}
      <Box sx={{ px: 1.5, py: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
          {data.label || data.blockType}
        </Typography>
        {data.summary && (
          <Typography variant="caption" color="text.secondary">
            {data.summary}
          </Typography>
        )}

        {/* Debug output indicator */}
        {debugLabel && (
          <Typography variant="caption" sx={{
            display: 'block',
            fontFamily: 'monospace',
            fontSize: '0.6rem',
            mt: 0.5,
            color: debugStep.status === 'completed' ? '#2e7d32' : '#c62828',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 160,
          }}>
            {debugLabel}
          </Typography>
        )}

        {debugStep?.error && (
          <Typography variant="caption" sx={{
            display: 'block',
            fontSize: '0.6rem',
            mt: 0.25,
            color: '#c62828',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 160,
          }}>
            {debugStep.error}
          </Typography>
        )}
      </Box>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: '#388e3c', width: 10, height: 10 }}
      />
    </Box>
  );
});

ActionNode.displayName = 'ActionNode';
export default ActionNode;
